
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    Divider
} from "@mui/material";
import styles from './progressBar.module.css';

const getColor = (key, type) => {
    if (type === 'coverage') {
        switch (key) {
            case 'Optimal': return '#A3E1D2';
            case 'Moderate': return '#FFEC9A';
            case 'Critical': return '#FF9998';
            default: return '#dcdde1';
        }
    }
    if (type === 'priority') {
        switch (key) {
            case 'Prio 1': return '#77DDF5';
            case 'Prio 2': return '#00B2FF';
            case 'Prio 3': return '#005EEF';
            default: return '#dcdde1';
        }
    }
    if (type === 'segmentation') {
        const segmentationStatusKeys = [
            { label: 'Agile', color: '#A5D6A7' },
            { label: 'Star', color: '#f7a3a1' },
            { label: 'Nurture', color: '#FFE082' },
            { label: 'Lean', color: '#B2DFDB' }
        ];
        const match = segmentationStatusKeys.find(item => item.label === key);
        return match ? match.color : '#E0E0E0';
    }

};

export default function FgCoverageTable({ label, data, loading = false }) {
    const [coverageType, setCoverageType] = useState('Phy FG Coverage');
    const [priorityType, setPriorityType] = useState('Prio 1');

    const handleCoverageTypeChange = (event, newType) => {
        if (newType !== null) {
            setCoverageType(newType);
        }
    };

    const handlePriorityTypeChange = (event, newType) => {
        if (newType !== null) {
            setPriorityType(newType);
        }
    };

    const weekKeys = data?.weeklyFgCoverage ? Object.keys(data.weeklyFgCoverage) : [];
    const segmentationWeeks = data?.physical ? Object.keys(data.physical) : [];


    return (
        <Box className={styles.container} sx={{ position: 'relative' }}>
            {loading && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 20,
                    }}
                >
                    <CircularProgress size={24} />
                </Box>
            )}
            <div className={styles.stackContainer}>
                <span className={styles.label}>
                    {label}
                </span>

                {/* Toggle for Segmentation */}
                {label === "Segmentation" && (
                    <ToggleButtonGroup
                        value={coverageType}
                        exclusive
                        onChange={handleCoverageTypeChange}
                        size="small"
                        sx={{ height: "20px" }}
                    >
                        {["Phy FG Coverage", "Total FG Coverage"].map((val) => (
                            <ToggleButton
                                key={val}
                                value={val}
                                className={`${styles.toggleButton} ${coverageType === val ? styles.toggleButtonSelected : ""}`}
                            >
                                {val}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                )}

                {/* Toggle for FG Coverage (Priority) */}
                {label === "FG Coverage (Priority)" && (
                    <ToggleButtonGroup
                        value={priorityType}
                        exclusive
                        onChange={handlePriorityTypeChange}
                        size="small"
                        sx={{ height: "20px" }}
                    >
                        {["Prio 1", "Prio 2", "Prio 3"].map((val) => (
                            <ToggleButton
                                key={val}
                                value={val}
                                className={`${styles.toggleButton} ${priorityType === val ? styles.toggleButtonSelected : ""}`}
                            >
                                {val}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                )}
            </div>

            {/* FG Coverage - Physical FG Coverage & Total FG Coverage */}
            {label === "FG Coverage" && (
                <div className={styles.fgCoverageContainer} style={{ display: 'flex', flexDirection: 'row' }}>
                    {[
                        { title: "Physical FG Coverage", key: "physicalCoverage" },
                        { title: "Total FG Coverage", key: "weeklyFgCoverage" }
                    ].map(({ title, key }, index) => (
                        <React.Fragment key={key}>
                            <div className={styles.weekContainer}>
                                <span className={styles.fgCoverageLabel}>{title}</span>
                                <TableContainer 
                                    component={Paper} 
                                    className={styles.tableContainer}
                                    sx={{
                                        '&::-webkit-scrollbar': {
                                            width: '7px',
                                            height: '7px',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: '#f1f1f1',
                                            borderRadius: '7px',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            borderRadius: '7px',
                                        }
                                    }}
                                >
                                    <Table size="small">
                                        <TableBody>
                                            {weekKeys.map((weekKey) => (
                                                <TableRow key={weekKey} sx={{ height: 20 }}>
                                                    <TableCell className={styles.segmentationLabel}>
                                                        {weekKey}
                                                    </TableCell>
                                                    {["Optimal", "Moderate", "Critical"].map((status) => (
                                                        <TableCell
                                                            key={status}
                                                            align="center"
                                                            className={styles.segmentationLabel}
                                                            sx={{
                                                                backgroundColor: getColor(status, "coverage")
                                                            }}
                                                        >
                                                            {data?.[key]?.[weekKey]?.[status] ?? 0}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                            {index === 0 && (
                                <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            )}


            {/* FG Coverage (Priority) - Physical FG Coverage & Total FG Coverage */}
            {label === "FG Coverage (Priority)" && (
                <div
                    className={styles.fgCoverageContainer}
                    style={{ display: "flex", flexDirection: "row", gap: "16px" }}
                >
                    {["physical_coverage", "total_coverage"].map((coverageType, index) => (
                        <React.Fragment key={coverageType}>
                            <div className={styles.weekContainer} style={{ flex: 1 }}>
                                <span className={styles.fgCoverageLabel}>
                                    {coverageType === "physical_coverage"
                                        ? "Physical FG Coverage"
                                        : "Total FG Coverage"}
                                </span>

                                <TableContainer 
                                    component={Paper} 
                                    className={styles.tableContainer}
                                    sx={{
                                        '&::-webkit-scrollbar': {
                                            width: '7px',
                                            height: '7px',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: '#f1f1f1',
                                            borderRadius: '7px',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            borderRadius: '7px',
                                        }
                                    }}
                                >
                                    <Table size="small" padding="none">
                                        <TableBody>
                                            {Object.keys(data?.[coverageType] || {}).map((weekKey) => (
                                                <TableRow key={weekKey} sx={{ height: 20 }}>
                                                    <TableCell className={styles.segmentationLabel}>
                                                        {weekKey}
                                                    </TableCell>
                                                    {["Critical", "Moderate", "Optimal"].map((status) => (
                                                        <TableCell
                                                            key={`${weekKey}-${status}`}
                                                            align="center"
                                                            className={styles.segmentationLabel}
                                                            sx={{
                                                                border: "none",
                                                                backgroundColor: getColor(status, "coverage"),
                                                            }}
                                                        >
                                                            {
                                                                data?.[coverageType]?.[weekKey]?.[priorityType]?.[
                                                                status
                                                                ] ?? 0
                                                            }
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>

                            {index === 0 && (
                                <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            )}


            {/* Segmentation Table */}
            {label === "Segmentation" && (
                <TableContainer 
                    component={Paper} 
                    className={styles.tableContainer}
                    sx={{
                        '&::-webkit-scrollbar': {
                            width: '7px',
                            height: '7px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                            borderRadius: '7px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            borderRadius: '7px',
                        }
                    }}
                >
                    <Table size="small" padding="none">
                        <TableHead >
                            <TableRow >
                                <TableCell className={styles.segmentationLabel}>Wk</TableCell>
                                <TableCell align="center" className={styles.segmentationLabel}>Lean</TableCell>
                                <TableCell align="center" className={styles.segmentationLabel}>Agile</TableCell>
                                <TableCell align="center" className={styles.segmentationLabel}>Nurture</TableCell>
                                <TableCell align="center" className={styles.segmentationLabel}>Star</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody >
                            {segmentationWeeks.map((weekKey) => {

                                const weekData = coverageType === 'Phy FG Coverage'
                                    ? data.physical?.[weekKey]
                                    : data.total?.[weekKey];

                                return (
                                    <TableRow key={weekKey} sx={{ height: 20 }}>
                                        <TableCell sx={{ mb: 2 }} className={styles.segmentationLabel}>{weekKey}</TableCell>
                                        {['Lean', 'Agile', 'Nurture', 'Star'].map(segment => {
                                            const segmentData = weekData?.[segment] || {};
                                            const percentageValue = segmentData.Percentage
                                                ? parseInt(segmentData.Percentage.replace('%', ''))
                                                : 0;

                                            return (
                                                <TableCell
                                                    key={segment}
                                                    align="center"
                                                    className={styles.segmentationLabel}
                                                    sx={{
                                                        backgroundColor: getColor(segment, 'segmentation')
                                                    }}
                                                >
                                                    {percentageValue}%
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )
            }
        </Box>
    );
}

FgCoverageTable.propTypes = {
    label: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    loading: PropTypes.bool,
};
